// Phase SIKA-5e/2 — SIKA feature flags (localStorage-backed).
//
// SSR-safe (returns default during prerender). Mirrors the pattern
// used by sidebar nest expansion (strikza:sidebar:sika:expanded).
//
// FLAGS:
//   SIKA_CHAT_V2 — Claude.ai-style chat surface. While ramping
//     (5e/2 → 5e/4), default is OFF. Set to '1' in localStorage
//     to opt in for visual QA. Flipped to default-ON in 5e/4,
//     removed entirely in 5e/5.

import { useEffect, useState } from "react";

const KEY_CHAT_V2 = "strikza:sika:chat-v2";

function readFlag(key: string, defaultValue: boolean): boolean {
  if (typeof window === "undefined") return defaultValue;
  try {
    const v = window.localStorage.getItem(key);
    if (v === null) return defaultValue;
    return v === "1" || v === "true";
  } catch {
    return defaultValue;
  }
}

function writeFlag(key: string, value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // localStorage may be disabled in private mode — silent no-op.
  }
}

/**
 * Read the SIKA_CHAT_V2 flag with SSR-safe default.
 *
 * @param defaultValue — what to return when the flag is unset
 *   (5e/2-3: false, 5e/4: true)
 */
export function useSikaChatV2(defaultValue = false): {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
} {
  const [enabled, setEnabled] = useState<boolean>(() =>
    readFlag(KEY_CHAT_V2, defaultValue),
  );

  // Re-read once on mount in case SSR returned the default but the
  // user had already opted in via DevTools.
  useEffect(() => {
    const live = readFlag(KEY_CHAT_V2, defaultValue);
    if (live !== enabled) setEnabled(live);
    // Listen for cross-tab changes.
    const onStorage = (e: StorageEvent): void => {
      if (e.key === KEY_CHAT_V2) {
        setEnabled(readFlag(KEY_CHAT_V2, defaultValue));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAndPersist = (v: boolean): void => {
    writeFlag(KEY_CHAT_V2, v);
    setEnabled(v);
  };

  return { enabled, setEnabled: setAndPersist };
}

/** Pure read helper for code paths outside React (tests, init). */
export function isSikaChatV2Enabled(defaultValue = false): boolean {
  return readFlag(KEY_CHAT_V2, defaultValue);
}

/** Test-only: clear the flag from localStorage. */
export function __resetSikaChatV2(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY_CHAT_V2);
  } catch {
    // noop
  }
}
